import { useState } from 'react';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import { useManualFulfill } from '../../api/pickListLifecycle';

export interface PickListManualFulfillBarProps {
  pickListId: number;
  status: string;
  /** Quante righe della lista sono ancora da prelevare. */
  pickableRows: number;
  /** Quante righe sono escluse dal prelievo. */
  excludedRows: number;
  /** Capo magazzino (o amministratore di piattaforma). */
  canEdit: boolean;
  warehouseIdHint?: number;
}

/**
 * Evasione manuale di una lista in cui **non c'è nulla da prelevare**.
 *
 * Compare solo quando *tutte* le righe sono in ubicazioni escluse: senza questo
 * comando quella lista resterebbe in *Ricevuta* per sempre — completa per
 * definizione, ma mai inviata a Book, perché non potendo essere divisa non
 * diventerebbe mai *Evasa*.
 *
 * ⚠️ **Con anche una sola riga prelevabile il comando non c'è.** È il vincolo
 * che lo rende innocuo: un pulsante generale "evadi a mano" permetterebbe di
 * dichiarare prelevata merce mai toccata, e prima o poi verrebbe usato per
 * chiudere una lista scomoda invece che per il caso che deve risolvere.
 */
export function PickListManualFulfillBar({
  pickListId, status, pickableRows, excludedRows, canEdit, warehouseIdHint,
}: PickListManualFulfillBarProps) {
  const { token, tenantCode } = useTenantContext();
  const mut = useManualFulfill(token, tenantCode, warehouseIdHint);
  const [chiedi, setChiedi] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  // Solo su liste arrivate e mai divise, con zero righe prelevabili e almeno
  // una riga in tutto (una lista vuota e' un altro problema).
  const nullaDaPrelevare = pickableRows === 0 && excludedRows > 0;
  if (status !== 'open' || !nullaDaPrelevare || !canEdit) return null;

  const evadi = async () => {
    setErrore(null);
    try {
      await mut.mutateAsync(pickListId);
      setChiedi(false);
    } catch (e: unknown) {
      // ⚠️ Nel progetto convivono DUE `authedFetch` con forme d'errore diverse:
      // quello delle pick-list lancia `LifecycleApiError`, che espone `code` e
      // `detail` DIRETTAMENTE; quello degli admin mette tutto sotto `.body`.
      // Leggere dal posto sbagliato fa cadere ogni messaggio sul generico.
      const err = e as { code?: string; detail?: string };
      setErrore(err?.detail ?? 'Non è stato possibile evadere la lista.');
    }
  };

  return (
    <div className="d-flex flex-column gap-2 mb-3 p-2 rounded border bg-body-tertiary">
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <span className="fw-semibold small">Niente da prelevare</span>
        <span className="small text-muted">
          Tutte le {excludedRows} righe sono in ubicazioni escluse: nessun operatore
          deve prelevarle.
        </span>
        <Button color="primary" size="sm" onClick={() => { setChiedi(true); setErrore(null); }}>
          <i className="bi bi-check2-circle me-1" />Evadi senza prelievo
        </Button>
      </div>
      {errore && <Alert color="danger" className="mb-0 py-1 px-2 small" role="alert">{errore}</Alert>}

      <Modal isOpen={chiedi} toggle={() => setChiedi(false)} centered>
        <ModalHeader toggle={() => setChiedi(false)}>Evadere senza prelievo?</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            La lista verrà dichiarata <strong>Evasa</strong> senza che nessun operatore
            abbia prelevato nulla.
          </p>
          <p className="mb-0 text-muted small">
            Tutte le {excludedRows} righe sono in ubicazioni escluse dal prelievo.
            Da qui la lista segue il percorso normale: attende colli e imballo,
            poi viene inviata a Book con le quantità piene.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setChiedi(false)}>Annulla</Button>
          <Button color="primary" disabled={mut.isPending} onClick={() => void evadi()}>
            {mut.isPending ? 'Evasione…' : 'Evadi senza prelievo'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
